import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DevicesIcon from '@mui/icons-material/Devices';
import EditIcon from '@mui/icons-material/Edit';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link as RouterLink } from 'react-router-dom';
import { sanitizePhoneNumber } from '../../services/cognitoService';
import { BRAND, dialogPaperProps, primaryButtonSx as primaryButtonStyle } from '../../styles/ui';
import {
  EmergencyContact,
  JoanProfile,
  addContact,
  removeContact,
  updateContact
} from '../../services/joanApi';

interface EmergencyContactsPanelProps {
  profile: JoanProfile | null;
  loading: boolean;
  loadError: string | null;
  onRetry: () => void;
  onChange: (profile: JoanProfile) => void;
  onAddDevice: () => void;
}

const noticeStyle = {
  backgroundColor: BRAND.inset,
  p: 3,
  borderRadius: 1,
  mb: 2,
  color: BRAND.muted
};

// Alerts go out by text message, so contacts must be UK mobile numbers
const contactValidationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .max(50, 'Name must be 50 characters or fewer')
    .required('Contact name is required'),
  phoneNumber: Yup.string()
    .required('Phone number is required')
    .test('is-uk-mobile', 'Enter a UK mobile number (07… or +447…)', (value) =>
      /^\+447\d{9}$/.test(sanitizePhoneNumber(value || ''))
    )
});

const EmergencyContactsPanel: React.FC<EmergencyContactsPanelProps> = ({
  profile,
  loading,
  loadError,
  onRetry,
  onChange,
  onAddDevice
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<EmergencyContact | null>(null);
  const [contactToDelete, setContactToDelete] = useState<EmergencyContact | null>(null);
  const [deleting, setDeleting] = useState(false);

  const contacts = profile?.contacts ?? [];
  const maxContacts = profile?.maxContacts ?? 3;

  const contactFormik = useFormik({
    initialValues: { name: '', phoneNumber: '' },
    validationSchema: contactValidationSchema,
    onSubmit: async (values) => {
      setError(null);
      setSuccess(null);
      try {
        const name = values.name.trim();
        const updated = editingContact
          ? await updateContact(editingContact.phone, name, values.phoneNumber)
          : await addContact(name, values.phoneNumber);
        onChange(updated);
        setSuccess(editingContact ? 'Emergency contact updated' : 'Emergency contact added');
        handleCloseDialog();
      } catch (err: any) {
        setError(err.message || 'Could not save the contact. Please try again.');
      }
    }
  });

  const handleOpenDialog = (contact?: EmergencyContact) => {
    setError(null);
    setEditingContact(contact ?? null);
    contactFormik.resetForm({
      values: contact ? { name: contact.name, phoneNumber: contact.phone } : { name: '', phoneNumber: '' }
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
    contactFormik.resetForm();
  };

  const handleConfirmDelete = async () => {
    if (!contactToDelete) return;
    setDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      onChange(await removeContact(contactToDelete.phone));
      setSuccess('Emergency contact removed');
    } catch (err: any) {
      setError(err.message || 'Could not remove the contact. Please try again.');
    } finally {
      setDeleting(false);
      setContactToDelete(null);
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (loadError || !profile) {
      return (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={onRetry}>Retry</Button>}
        >
          {loadError || 'Could not load your emergency contacts.'}
        </Alert>
      );
    }

    if (profile.deviceStatus === 'none') {
      return (
        <Box sx={noticeStyle}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Link your Joan device to your account before adding emergency contacts.
          </Typography>
          <Button variant="contained" startIcon={<DevicesIcon />} onClick={onAddDevice} sx={primaryButtonStyle}>
            Add device ID
          </Button>
        </Box>
      );
    }

    if (profile.deviceStatus === 'shared') {
      return (
        <Alert severity="warning">
          Your device ID is linked to more than one account, so emergency contacts can't be managed here.
          Please <Link component={RouterLink} to="/contact" sx={{ color: 'inherit', fontWeight: 600 }}>contact support</Link>.
        </Alert>
      );
    }

    return (
      <>
        {contacts.length === 0 ? (
          <Box sx={noticeStyle}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              No emergency contacts added yet.
            </Typography>
            <Typography variant="body2">
              Add contacts who should be notified in case of emergency.
            </Typography>
          </Box>
        ) : (
          <List sx={{ backgroundColor: BRAND.inset, borderRadius: 1, overflow: 'hidden' }}>
            {contacts.map((contact, index) => (
              <React.Fragment key={contact.phone}>
                {index > 0 && <Divider sx={{ borderColor: BRAND.line }} />}
                <ListItem sx={{ pr: '112px' }}>
                  <ListItemText
                    primary={<Typography sx={{ color: BRAND.ink }}>{contact.name}</Typography>}
                    secondary={
                      <>
                        <Typography component="span" sx={{ color: BRAND.muted, display: 'block' }}>
                          {contact.phone}
                        </Typography>
                        {!contact.alerting && (
                          <Typography component="span" variant="body2" sx={{ color: BRAND.warning, display: 'block' }}>
                            Not receiving alerts yet — edit and save this contact to fix it.
                          </Typography>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label={`Edit ${contact.name}`}
                      onClick={() => handleOpenDialog(contact)}
                      sx={{ color: BRAND.muted }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label={`Remove ${contact.name}`}
                      onClick={() => setContactToDelete(contact)}
                      sx={{ ml: 1, color: BRAND.muted }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={contacts.length >= maxContacts}
            sx={{ ...primaryButtonStyle, width: { xs: '100%', sm: 'auto' } }}
          >
            Add Contact
          </Button>
        </Box>
      </>
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="body1" sx={{ color: BRAND.ink, mb: 2 }}>
          Add up to {maxContacts} emergency contacts who will be notified by text message in case of an emergency.
        </Typography>

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {error && !dialogOpen && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderBody()}
      </Box>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={dialogOpen} onClose={contactFormik.isSubmitting ? undefined : handleCloseDialog} PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ color: BRAND.ink }}>
          {editingContact ? 'Edit Emergency Contact' : 'Add Emergency Contact'}
        </DialogTitle>
        <form onSubmit={contactFormik.handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              margin="dense"
              id="name"
              name="name"
              label="Contact Name"
              fullWidth
              value={contactFormik.values.name}
              onChange={contactFormik.handleChange}
              error={contactFormik.touched.name && Boolean(contactFormik.errors.name)}
              helperText={contactFormik.touched.name && contactFormik.errors.name}
              disabled={contactFormik.isSubmitting}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              id="phoneNumber"
              name="phoneNumber"
              label="Mobile Number"
              type="tel"
              fullWidth
              placeholder="07… or +447…"
              value={contactFormik.values.phoneNumber}
              onChange={contactFormik.handleChange}
              error={contactFormik.touched.phoneNumber && Boolean(contactFormik.errors.phoneNumber)}
              helperText={contactFormik.touched.phoneNumber && contactFormik.errors.phoneNumber}
              disabled={contactFormik.isSubmitting}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCloseDialog}
              disabled={contactFormik.isSubmitting}
              sx={{ color: BRAND.ink, '&:hover': { backgroundColor: BRAND.hover } }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={contactFormik.isSubmitting} sx={primaryButtonStyle}>
              {contactFormik.isSubmitting ? <CircularProgress size={24} /> : editingContact ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Remove Contact Confirmation */}
      <Dialog open={Boolean(contactToDelete)} onClose={deleting ? undefined : () => setContactToDelete(null)} PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ color: BRAND.ink }}>Remove emergency contact?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: BRAND.muted }}>
            {contactToDelete?.name} ({contactToDelete?.phone}) will no longer be alerted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setContactToDelete(null)}
            disabled={deleting}
            sx={{ color: BRAND.ink, '&:hover': { backgroundColor: BRAND.hover } }}
          >
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmergencyContactsPanel;
